import { apiSlice } from '../../apiSlice';

export const requestApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Mutation to submit a general request (e.g., item return, rental-to-purchase)
    submitRequest: builder.mutation({
      query: (requestData) => ({
        url: '/requests',
        method: 'POST',
        body: requestData,
      }),
      // Invalidates tags to refetch lists of requests or related data
      invalidatesTags: (result, error, arg) => {
        // Start with basic invalidations
        const tags = [
        { type: 'Request', id: 'LIST' },
        // Potentially invalidate other tags based on requestType and related entities
          arg.relatedOrderId ? { type: 'Order', id: arg.relatedOrderId } : { type: 'Order', id: 'LIST' },
          arg.relatedFulfillmentGroupId ? { type: 'FulfillmentGroup', id: arg.relatedFulfillmentGroupId } : { type: 'FulfillmentGroup', id: 'LIST' },
          arg.relatedOrderItemComponentId ? { type: 'OrderItem', id: arg.relatedOrderItemComponentId } : { type: 'OrderItem', id: 'LIST' },
        ];
        
        // If we have a result, also invalidate based on the created entity
        if (result) {
          // If the request was created successfully, also invalidate based on the returned data
          if (result.id) {
            tags.push({ type: 'Request', id: result.id });
          }
          
          // If it's an item_return request, forcefully invalidate all related entities
          if (arg.requestType === 'item_return') {
            // Ensure Order and FulfillmentGroup lists are invalidated to refresh dashboard views
            tags.push({ type: 'Order', id: 'LIST' });
            tags.push({ type: 'FulfillmentGroup', id: 'LIST' });
            tags.push({ type: 'OrderItem', id: 'LIST' });
            
            // If we have a specific OrderItem ID from the result, invalidate it specifically
            if (result.relatedOrderItemComponentId) {
              tags.push({ type: 'OrderItem', id: result.relatedOrderItemComponentId });
            }
            
            // If we have a FulfillmentGroup from the result, add specific tag for it
            if (result.relatedFulfillmentGroup?.id) {
              tags.push({ type: 'FulfillmentGroup', id: result.relatedFulfillmentGroup.id });
              // Also add a special tag for OrderItems in this FG
              tags.push({ type: 'OrderItem', id: `FG_${result.relatedFulfillmentGroup.id}` });
            }
          }
        }
        
        return tags;
      },
    }),

    // Query to get requests submitted by the current user
    getMyRequests: builder.query({
      query: () => '/requests/me',
      providesTags: (result = []) => [
        { type: 'Request', id: 'LIST' },
        ...result.map(request => ({ type: 'Request', id: request.id }))
      ],
    }),

    // Query to get requests for the current merchant (e.g., pending approval)
    getMerchantRequests: builder.query({
      query: (params) => ({
        url: '/requests/merchant', // Assumes a backend route like /requests/merchant
        params, // e.g., { status: 'pending_approval' }
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Request', id })), { type: 'Request', id: 'LIST' }]
          : [{ type: 'Request', id: 'LIST' }],
    }),

    // Mutation to update a request (e.g., approve, reject by merchant)
    updateRequest: builder.mutation({
      query: ({ requestId, ...patch }) => ({
        url: `/requests/${requestId}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { requestId }) => {
        if (error) return [{ type: 'Request', id: requestId }];
        // Access related entities if available in the response
        const fulfillmentGroupId = result?.relatedFulfillmentGroup?.id;
        const orderId = result?.relatedOrder?.id;
        const orderItemId = result?.relatedOrderItemComponentId;
        const tags = [
        { type: 'Request', id: requestId }, 
        { type: 'Request', id: 'LIST' }
        ];
        
        // Add invalidations if we have their IDs
        if (fulfillmentGroupId) {
          tags.push({ type: 'FulfillmentGroup', id: fulfillmentGroupId });
          tags.push({ type: 'FulfillmentGroup', id: 'LIST' });
        }
        
        if (orderId) {
          tags.push({ type: 'Order', id: orderId });
          tags.push({ type: 'Order', id: 'LIST' });
        }
        
        if (orderItemId) {
          tags.push({ type: 'OrderItem', id: orderItemId });
          tags.push({ type: 'OrderItem', id: 'LIST' });
        }
        
        return tags;
      },
    }),

    // Query to get a specific request by ID
    getRequestById: builder.query({
        query: (requestId) => `/requests/${requestId}`,
        providesTags: (result, error, requestId) => [{ type: 'Request', id: requestId }],
    }),

    // Mutations to handle return approvals
    approveItemReturn: builder.mutation({
      query: ({ requestId, relatedOrderItemId }) => ({
        url: `/requests/approve-return`,
        method: 'POST',
        body: { requestId, relatedOrderItemId }
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Request', id: arg.requestId },
        { type: 'Request', id: `orderItem-${arg.relatedOrderItemId}` }, // For getReturnRequestByOrderItem
        { type: 'OrderItem', id: arg.relatedOrderItemId },
        { type: 'Request', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' }, // Invalidate FG list to refresh dashboard
      ],
    }),

    // Mutations to handle return rejections
    rejectItemReturn: builder.mutation({
      query: ({ requestId, relatedOrderItemId, rejectionReason }) => ({
        url: `/requests/reject-return`,
        method: 'POST',
        body: { requestId, relatedOrderItemId, rejectionReason }
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Request', id: arg.requestId },
        { type: 'Request', id: `orderItem-${arg.relatedOrderItemId}` }, // For getReturnRequestByOrderItem
        { type: 'OrderItem', id: arg.relatedOrderItemId },
        { type: 'Request', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' }, // Invalidate FG list to refresh dashboard
      ],
    }),

    // Submit a group return request for all items in a fulfillment group
    submitGroupReturnRequest: builder.mutation({
      query: ({ fulfillmentGroupId, orderItemIds, reason, comments }) => ({
        url: `/requests/group-return`,
        method: 'POST',
        body: { fulfillmentGroupId, orderItemIds, reason, comments }
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'FulfillmentGroup', id: arg.fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Add new endpoint to the requestApi.endpoints object
    getReturnRequestByOrderItem: builder.query({
      query: (orderItemId) => `/requests/return/${orderItemId}`,
      providesTags: (result, error, orderItemId) => [{ type: 'Request', id: `orderItem-${orderItemId}` }],
    }),

    // NEW: Group return approval/rejection mutations

    // Approve all returns in a group (merchant action)
    approveGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId, approvalComments }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/approve-all-returns`,
        method: 'POST',
        body: { approvalComments }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Reject all returns in a group (merchant action)
    rejectGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId, rejectionReason }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/reject-all-returns`,
        method: 'POST',
        body: { rejectionReason }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Get all return requests for a fulfillment group
    getFulfillmentGroupReturnRequests: builder.query({
      query: (fulfillmentGroupId) => `/fulfillment-groups/${fulfillmentGroupId}/return-requests`,
      providesTags: (result = [], error, fulfillmentGroupId) => [
        { type: 'Request', id: `FG_${fulfillmentGroupId}` },
        { type: 'Request', id: 'LIST' },
        ...result.map(request => ({ type: 'Request', id: request.id }))
      ],
    }),

    // Submit a keep items request for non-sale items
    submitKeepItemsRequest: builder.mutation({
      query: ({ fulfillmentGroupId, subgroupId, orderItemIds, reason, comments }) => {
        // Only include reason and comments if they are provided
        const body = { fulfillmentGroupId, subgroupId, orderItemIds };
        if (reason) body.reason = reason;
        if (comments) body.comments = comments;
        
        return {
          url: `/requests/keep-items`,
          method: 'POST',
          body
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'FulfillmentGroup', id: arg.fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        // Add specific order item tags for the updated items
        ...arg.orderItemIds.map(itemId => ({ type: 'OrderItem', id: itemId }))
      ],
    }),

    // Process keep items requests (merchant approval/rejection with pricing)
    processKeepItemsRequest: builder.mutation({
      query: ({ fulfillmentGroupId, decisions }) => ({
        url: `/requests/process-keep-items`,
        method: 'POST',
        body: { fulfillmentGroupId, decisions }
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'FulfillmentGroup', id: arg.fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        // Add specific order item tags for the processed items
        ...arg.decisions.map(decision => ({ type: 'OrderItem', id: decision.itemId }))
      ],
    }),

  }),
});

export const {
  useSubmitRequestMutation,
  useGetMyRequestsQuery,
  useGetMerchantRequestsQuery,
  useUpdateRequestMutation,
  useGetRequestByIdQuery,
  useApproveItemReturnMutation,
  useRejectItemReturnMutation,
  useSubmitGroupReturnRequestMutation,
  useGetReturnRequestByOrderItemQuery,
  useApproveGroupReturnMutation,
  useRejectGroupReturnMutation,
  useGetFulfillmentGroupReturnRequestsQuery,
  useSubmitKeepItemsRequestMutation,
  useProcessKeepItemsRequestMutation,
} = requestApi; 